import { type ReactNode, useMemo } from 'react'
import { isEmpty, isNotEmpty, type ProgramAutoEnrollmentTrigger, type TeamId } from '@terros/common'
import { Stack } from '@mantine/core'
import { useTeams } from '@front/model'
import { TAlert } from '@components'
import { isTeamTriggerReachable } from './triggerScope'

type Props = {
  triggers: ProgramAutoEnrollmentTrigger[]
  scopeTeamIds: TeamId[] | undefined
}

export const ProgramTriggerWarnings = ({ triggers, scopeTeamIds }: Props): ReactNode => {
  const unreachableTeamNames = useUnreachableTriggerTeamNames({ triggers, scopeTeamIds })
  const isScoped = isNotEmpty(scopeTeamIds)
  const showUserCreatedWarning = isScoped && triggers.some((trigger) => trigger.type === 'userCreated')

  if (!showUserCreatedWarning && isEmpty(unreachableTeamNames)) return

  return (
    <Stack gap='xs'>
      {showUserCreatedWarning && (
        <TAlert variant='warning' title='Terros User Start Date will not enroll anyone'>
          This trigger fires before the user joins a team, so it never matches while a scope is set. Clear the scope, or
          remove this trigger.
        </TAlert>
      )}
      {isNotEmpty(unreachableTeamNames) && (
        <TAlert
          variant='warning'
          title={`Team Assignment will not enroll anyone for ${unreachableTeamNames.join(', ')}`}
        >
          Users joining these teams fall outside the selected scope. Choose a scope on the same branch as the team, or
          clear the scope.
        </TAlert>
      )}
    </Stack>
  )
}

const useUnreachableTriggerTeamNames = ({ triggers, scopeTeamIds }: Props): string[] => {
  const { data: teams } = useTeams({ showArchived: 'all' })

  return useMemo(() => {
    if (isEmpty(scopeTeamIds) || isEmpty(teams)) return []
    return triggers.flatMap((trigger) => {
      if (trigger.type !== 'team') return []
      if (isTeamTriggerReachable({ triggerTeamId: trigger.teamId, scopeTeamIds, teams })) return []
      return [teams.find((team) => team.teamId === trigger.teamId)?.name ?? trigger.teamId]
    })
  }, [triggers, scopeTeamIds, teams])
}
